/**
 * Volet d'une entité commerciale : statut, revenu, effectif, facturation, notes.
 *
 * Le même volet sert un GMF et un département d'hôpital — ce sont les mêmes
 * champs, parce que ce sont les mêmes objets commerciaux. Seul l'endroit change :
 * dans la fiche pour un GMF, dans la ligne dépliée pour un département.
 *
 * Un volet est CRÉÉ UNE FOIS par entité puis mis à jour, jamais recréé.
 * Recréer l'élément à chaque rendu détruirait le focus : une flèche ↑ dans le
 * champ d'effectif déclenche un rendu, et le curseur sauterait hors du champ à
 * chaque pas.
 */

import {
  MAX_PAYMENT_DAY,
  MIN_PAYMENT_DAY,
  nextPaymentDate,
  paymentDayOf,
} from '../domain/billing.js'
import { estimatePros, isEstimate, usersOf } from '../domain/estimate.js'
import { recordKey } from '../domain/record.js'
import { totalEarned } from '../domain/revenue.js'
import {
  getNotes,
  getState,
  addNote as addNoteToState,
  removeNote as removeNoteFromState,
} from '../state.js'
import { addNote, deleteNote } from '../supabase/records.js'
import {
  $,
  escapeHtml,
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatNumber,
  formatOrdinalDay,
  money,
  plural,
  show,
} from './dom.js'
import { withToast } from './toast.js'

/** Volets déjà construits, par entité. Voir l'en-tête : on ne recrée jamais. */
const panes = new Map()

/** « prenom@exemple.com » -> « prenom » : le domaine est le même pour tous, il n'apprend rien. */
const shortAuthor = (email) => (email ? email.split('@')[0] : 'inconnu')

function buildPayDayOptions(select) {
  const fragment = document.createDocumentFragment()

  const auto = document.createElement('option')
  auto.value = ''
  auto.textContent = 'date de début'
  fragment.appendChild(auto)

  for (let day = MIN_PAYMENT_DAY; day <= MAX_PAYMENT_DAY; day++) {
    const option = document.createElement('option')
    option.value = String(day)
    option.textContent = formatOrdinalDay(day)
    fragment.appendChild(option)
  }
  select.replaceChildren(fragment)
}

/**
 * Construit le volet d'une entité et le câble.
 *
 * @param entity   {kind, id, parentId?, nom, type?, inscrits?}
 * @param onPatch  (patch, {defer}) -> void — l'appelant écrit dans l'état
 */
function createPane(entity, onPatch) {
  const el = $('paneTpl').content.firstElementChild.cloneNode(true)
  const q = (sel) => el.querySelector(sel)

  // Identifiants uniques par instance : sans eux, plusieurs volets ouverts
  // auraient les mêmes, et cliquer un libellé activerait le champ d'un autre
  // département.
  const uid = recordKey(entity.kind, entity.id).replace(':', '-')
  for (const [field, label] of [['pros', 'proslbl'], ['since', 'sincelbl'], ['payday', 'paydaylbl']]) {
    q(`.js-${field}`).id = `${uid}-${field}`
    q(`.js-${label}`).htmlFor = `${uid}-${field}`
  }

  buildPayDayOptions(q('.js-payday'))

  q('.js-status').addEventListener('change', (e) => {
    const status = e.target.value
    const record = currentRecord(entity)
    const patch = { status }
    // Devenir client date de maintenant, sauf si la date est déjà connue :
    // sans elle, le cumul « gagné à ce jour » ne peut pas se calculer, et
    // personne ne pense à la saisir après coup.
    if ((status === 'client' || status === 'gratuit') && !record.since) {
      patch.since = new Date().toISOString().slice(0, 10)
    }
    onPatch(patch)
  })

  // `input` et non `change` : sur un input[type=number], `change` n'arrive
  // qu'à la sortie du champ ou après un pas. L'envoi différé absorbe la rafale
  // des flèches.
  q('.js-pros').addEventListener('input', (e) => {
    const raw = e.target.value.trim()
    // Champ vidé -> null : l'estimation reprend la main. Distinct d'un 0 saisi.
    onPatch({ pros: raw === '' ? null : Number.parseFloat(raw) }, { defer: true })
  })

  q('.js-since').addEventListener('change', (e) => onPatch({ since: e.target.value || null }))

  q('.js-payday').addEventListener('change', (e) => {
    // '' = « suivre la date de début » : on efface la saisie plutôt que d'y
    // figer une valeur, pour que déplacer la date de début suive.
    onPatch({ paymentDay: e.target.value === '' ? null : Number.parseInt(e.target.value, 10) })
  })

  const submitNote = async () => {
    const { user } = getState()
    if (!user) return
    const textarea = q('.js-note')
    const body = textarea.value.trim()
    if (!body) return

    textarea.value = ''
    const note = await withToast(addNote(entity, body, user), { failure: 'Note non enregistrée.' })
    if (note) addNoteToState(note)
    else textarea.value = body // échec : la note revient plutôt que d'être perdue
  }

  q('.js-noteadd').addEventListener('click', submitNote)
  q('.js-note').addEventListener('keydown', (e) => {
    // Cmd/Ctrl+Entrée envoie ; Entrée seule reste un retour à la ligne, une
    // note d'appel tenant rarement sur une ligne.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submitNote()
    }
  })

  return el
}

function currentRecord(entity) {
  return (
    getState().records.get(recordKey(entity.kind, entity.id)) || {
      kind: entity.kind,
      id: entity.id,
      name: entity.nom,
      parentId: entity.parentId ?? null,
      status: 'prospect',
      pros: null,
      since: null,
      paymentDay: null,
    }
  )
}

/** Le volet d'une entité, construit à la première demande. */
export function paneFor(entity, onPatch) {
  const key = recordKey(entity.kind, entity.id)
  let el = panes.get(key)
  if (!el) {
    el = createPane(entity, onPatch)
    panes.set(key, el)
  }
  return el
}

/** Oublie les volets dont l'entité a disparu — un département supprimé, par exemple. */
export function forgetPane(kind, id) {
  panes.delete(recordKey(kind, id))
}

// --- Rendu -----------------------------------------------------------------

function renderNotes(el, entity) {
  const box = el.querySelector('.js-notes')
  const { user } = getState()
  const fragment = document.createDocumentFragment()

  for (const note of getNotes(entity.kind, entity.id)) {
    const item = document.createElement('div')
    item.className = 'item'
    const mine = note.authorId && user && note.authorId === user.id

    item.innerHTML =
      // Supprimer n'est proposé que sur ses propres notes — RLS le refuserait
      // de toute façon, autant ne pas proposer un geste voué à l'échec.
      (mine ? '<button class="idel" title="Supprimer">&times;</button>' : '') +
      `<div class="nt"><span class="nauthor">${escapeHtml(shortAuthor(note.authorEmail))}</span> · ${formatDateTime(note.createdAt)}</div>` +
      `<div class="ntx">${escapeHtml(note.body)}</div>`

    if (mine) {
      item.querySelector('.idel').addEventListener('click', async () => {
        removeNoteFromState(note.id)
        await withToast(deleteNote(note.id), { failure: 'Suppression impossible.' })
      })
    }
    fragment.appendChild(item)
  }

  box.replaceChildren(fragment)
}

/** Remplit un volet. Ne recrée rien : voir l'en-tête du module. */
export function renderPane(el, entity) {
  const { settings } = getState()
  const record = currentRecord(entity)
  const tarif = Number.parseFloat(settings.tarif) || 0
  const users = usersOf(entity, record, settings.ratioPro)
  const status = record.status

  const q = (sel) => el.querySelector(sel)
  const heroVal = q('.js-heroval')
  const heroSub = q('.js-herosub')

  const estimated = isEstimate(entity, record, settings.ratioPro) ? ' (estimé)' : ''
  const noun = entity.kind === 'dept' ? "d'utilisateurs" : 'de professionnels'
  const unit = entity.kind === 'dept' ? 'utilisateur' : 'professionnel'
  const unitPlural = plural(users, unit)
  const missing = tarif <= 0 ? ' et le tarif (⚙)' : ''

  // Tout montant est déjà dans la couleur de l'argent : seul le partenaire, qui
  // affiche un mot et non une somme, a besoin d'une classe.
  heroVal.className = 'hero-val js-heroval'

  if (status === 'client') {
    heroVal.textContent = tarif > 0 && users > 0 ? money(users * tarif) : '—'
    heroSub.textContent =
      tarif > 0 && users > 0
        ? `de revenu mensuel · ${users} ${unitPlural}${estimated}`
        : `revenu mensuel — indique le nombre ${noun}${missing}`
  } else if (status === 'gratuit') {
    heroVal.classList.add('c-gratuit')
    heroVal.textContent = 'Partenaire'
    heroSub.textContent = users > 0 ? `${users} ${unitPlural}${estimated} · aucun revenu` : 'aucun revenu'
  } else {
    heroVal.textContent = tarif > 0 && users > 0 ? money(users * tarif) : '—'
    heroSub.textContent =
      tarif > 0 && users > 0
        ? `revenu potentiel · ${users} ${unitPlural} estimé${users > 1 ? 's' : ''}`
        : `revenu potentiel — indique le nombre ${noun}${missing}`
  }

  show(q('.js-sincerow'), status === 'client' || status === 'gratuit')
  // Seul un client payant a un jour de facturation : un partenaire ne paie pas,
  // un prospect n'a rien signé.
  show(q('.js-paydayrow'), status === 'client')
  q('.js-proslbl').textContent =
    (status === 'client' || status === 'gratuit' ? 'Nombre ' : 'Nombre estimé ') + noun

  q('.js-status').value = status
  q('.js-statdot').className = `statdot js-statdot d-${status}`
  q('.js-since').value = record.since || ''

  // Ne pas écrire dans un champ en cours de saisie : le curseur sauterait.
  const pros = q('.js-pros')
  if (document.activeElement !== pros) pros.value = record.pros ?? ''
  const estimate = estimatePros(entity, settings.ratioPro)
  // L'estimation en indice montre ce que vaudrait le champ laissé vide.
  pros.placeholder = estimate > 0 ? String(estimate) : ''

  // Réservé aux GMF : seuls eux ont une clientèle inscrite publiée par la RAMQ.
  const refLine = q('.js-refline')
  if (estimate > 0) {
    refLine.textContent =
      `≈ ${estimate} professionnels estimés · ${formatNumber(entity.inscrits)} patients inscrits ` +
      '(RAMQ/MSSS, mars 2025)'
    show(refLine, true)
  } else {
    show(refLine, false)
  }

  const payDay = q('.js-payday')
  if (document.activeElement !== payDay) {
    payDay.value = record.paymentDay == null ? '' : String(record.paymentDay)
  }
  // L'option automatique annonce le jour qu'elle donnerait. paymentDayOf(record)
  // rendrait la saisie explicite quand il y en a une, donc afficherait le jour
  // déjà choisi : on interroge la date de début seule.
  payDay.options[0].textContent = record.since
    ? `date de début (${formatOrdinalDay(paymentDayOf({ since: record.since }))})`
    : 'date de début'

  const totRow = q('.js-totrow')
  const earned = totalEarned(record, users, tarif)
  if (earned) {
    q('.js-totval').textContent = money(earned.total)
    q('.js-totsub').textContent =
      `depuis le ${formatDate(record.since)} · ` +
      `${earned.payments} ${plural(earned.payments, 'versement')} ${formatOrdinalDay(earned.paymentDay)}`
    const next = nextPaymentDate(record.since, earned.paymentDay)
    q('.js-totnext').textContent = next ? `prochain le ${formatDayMonth(next)}` : ''
    show(totRow, true)
  } else {
    show(totRow, false)
  }

  renderNotes(el, entity)
}
